import { inject, Injectable } from '@angular/core';
import { StateContext } from '@ngxs/store';
import { catchError, tap, throwError } from 'rxjs';
import { TableStateModel } from '../table.state.model';
import { Pagination } from '@core/models/pagination.model';
import { TableActions } from '../table.actions';
import { TransactionsService } from '@core/services/transactions.service';
import { NotificationFacade } from '@core/facades/notification.facade';

@Injectable({
  providedIn: 'root',
})
export class WithdrawalRequestsHandler {
  transactionService = inject(TransactionsService);
  notify = inject(NotificationFacade);

  handleWithdrawalRequestsLoad(
    ctx: StateContext<TableStateModel>,
    params: Pagination
  ) {
    const query = `take=${params.size}&skip=${params.skip}`;
    this.transactionService.getWalletWithdrawalRequests(query).subscribe({
      next: (response) => {
        ctx.dispatch(
          new TableActions.LoadTableDataSuccess({
            tableKey: 'withdrawalRequests',
            data: response.data?.data,
            totalCount: response.data?.total_count,
          })
        );
      },
      error: (error) => {
        ctx.dispatch(
          new TableActions.LoadTableDataFailure({
            tableKey: 'withdrawalRequests',
            error: error.message,
          })
        );
      },
    });
  }

  filterWithdrawalRequestsList(
    ctx: StateContext<TableStateModel>,
    action: TableActions.FilterWithdrawalRequestsList
  ) {
    return this.transactionService
      .getWalletWithdrawalRequests(action.payload.filter)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new TableActions.LoadTableDataSuccess({
              tableKey: 'withdrawalRequests',
              data: response.data?.data,
              totalCount: response.data?.total_count,
            })
          );
        },
        error: (error) => {
          ctx.dispatch(
            new TableActions.LoadTableDataFailure({
              tableKey: 'withdrawalRequests',
              error: error.message,
            })
          );
        },
      });
  }

  processWithdrawalRequest(
    ctx: StateContext<TableStateModel>,
    action: TableActions.ProcessWithdrawalRequest
  ) {
    return this.transactionService
      .processWalletWithdrawalRequest({
        withdrawal_request_id: action.payload.requestId,
        status: action.payload.status,
        account_id: action.payload.account_id,
        reason: action.payload.reason,
      })
      .pipe(
        tap(() => {
          const state = ctx.getState();
          const updated = state.withdrawalRequests?.data.map((request) =>
            request.id === action.payload.requestId
              ? { ...request, status: action.payload.status }
              : request
          );

          ctx.patchState({
            withdrawalRequests: {
              ...state.withdrawalRequests,
              data: updated,
            },
          } as Partial<TableStateModel>);

          this.notify.success(
            action.payload.status === 'completed'
              ? 'Withdrawal request approved successfully'
              : 'Withdrawal request declined successfully'
          );
        }),
        catchError((error) => {
          this.notify.error('Failed to process withdrawal request');
          return throwError(() => error);
        })
      );
  }
}
